import { ServiceContainerResult } from "@/services/backend/qpm/calculations/types/calculation";
import {
  CalculationServiceEntryType,
  CalculationServiceType,
} from "@/services/backend/qpm/calculations/types/calculation-enum";
import { CalculationServiceEntryUtil } from "@/services/backend/qpm/calculations/lib/calculation-service-entry-util";
import t from "@/lang/lang";
import { Value } from "@/shared/components/domain/qpm/inputs/number-input";
import { fMoney } from "@/routes/processes/_components/ui/number-input";
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@/shared/components/ui/table";

export type MaintenanceOverviewComponentProps = {
  results: ServiceContainerResult[];
};

export function MaintenanceOverviewComponent({
  results,
}: MaintenanceOverviewComponentProps) {
  // Define the custom order for CalculationServiceEntryType
  const customOrder = [
    CalculationServiceEntryType.CheckPoint,
    CalculationServiceEntryType.CheckPointVisual,
    CalculationServiceEntryType.CheckPointIoT,
    CalculationServiceEntryType.CheckPointSCB,
  ];

  // Group results by serviceEntryType and sort each group by amountOfMeasures
  const resultEntryMap: Record<
    CalculationServiceEntryType,
    ServiceContainerResult[]
  > = (results || []).reduce(
    (acc, result) => {
      if (!acc[result.serviceEntryType]) {
        acc[result.serviceEntryType] = [];
      }
      acc[result.serviceEntryType].push(result);
      return acc;
    },
    {} as Record<CalculationServiceEntryType, ServiceContainerResult[]>,
  );

  // Sort results within each group by amountOfMeasures
  Object.keys(resultEntryMap).forEach((type) => {
    const entryType = type as CalculationServiceEntryType;
    resultEntryMap[entryType].sort(
      (a, b) => a.amountOfMeasures - b.amountOfMeasures,
    );
  });

  const rows = customOrder.flatMap((type) => {
    const sortedEntries = resultEntryMap[type] || [];
    if (sortedEntries.length === 0) {
      return [
        {
          entry: {
            id: `empty-${type}`,
            serviceEntryType: type,
            amountOfMeasures: 0,
            amountOfMonitors: 0,
            totalCost: 0,
            costPerMeasure: 0,
            serviceGroupIds: [],
            monitorIds: [],
          } as ServiceContainerResult,
          type,
        },
      ];
    }
    return sortedEntries.map((entry) => ({ entry, type }));
  });

  return (
    <Table>
      <TableBody>
        {rows.map(({ entry, type }) => (
          <TableRow key={entry.id}>
            <TableCell className="text-muted-foreground">
              {CalculationServiceEntryUtil.toLongString(
                type,
                CalculationServiceType.Maintenance,
              )}
            </TableCell>
            <TableCell>
              <div className="flex items-center justify-end gap-2">
                <span className="text-muted-foreground">
                  {t("Anzahl Maßnahmen / Jahr")}
                </span>
                <span className="text-base font-semibold">
                  <Value value={entry.amountOfMeasures ?? 0} fractions={0} />
                </span>
              </div>
            </TableCell>
            <TableCell className="text-end">
              <div className="flex items-center justify-end gap-2">
                <span className="text-muted-foreground">
                  {t("pro Maßnahme")}
                </span>
                <span className="text-base font-semibold">
                  {fMoney(entry.costPerMeasure ?? 0)}
                </span>
              </div>
            </TableCell>
            <TableCell className="text-end">
              <div className="flex items-center justify-end gap-2">
                <span className="text-muted-foreground">{t("Gesamt")}</span>
                <span className="text-base font-semibold">
                  {fMoney(entry.totalCost ?? 0)}
                </span>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
